import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Input } from 'semantic-ui-react';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';
import { DateField } from '../shared/formFields';
import {
  MotifButton,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifToast,
} from '@ey-xd/motif-react';
import { useSelector } from 'react-redux';

const EndPropertyForm = ({
  handleSubmit,
  handleCloseEndForm,
  initialValues,
  errorMessage,
  submitting,
  handleDismissErrorMessage,
  onSave,
}) => {
  const HeaderWrapper = ({ children }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'center',
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </div>
  );

  const closeButtonProps = {
    title: 'Close End Property Modal',
    ariaLabel: 'Close End Property Modal',
    dataTestid: 'close',
  };

  const [endDateValidationError, setEndDateValidationError] = useState('');
  const selector = formValueSelector('property');
  const acquisitionDate = useSelector((state) =>
    selector(state, 'acquisitionDate')
  );
  const endDate = useSelector((state) => selector(state, 'endDate'));

  // Add validation between acquisition date and end date
  useEffect(() => {
    if (acquisitionDate && endDate) {
      const acquisitionDateObj = new Date(acquisitionDate);
      const endDateObj = new Date(endDate);

      // Check if acquisition date and end date is in valid date format
      let isAcquisitionDateValid =
        formValidators.isValidDate(acquisitionDateObj);
      let isEndDateValid = formValidators.isValidDate(endDateObj);

      // Check if acquisition date is valid
      // formValidators.isValidDate returns undefined if valid, otherwise it returns an error message
      if (isAcquisitionDateValid !== undefined) {
        setEndDateValidationError('Acquisition Date must be a valid date.');
        return;
      }

      // Compare acquisition date and end date
      // Check if end date is valid
      // formValidators.isValidDate returns undefined if valid, otherwise it returns an error message
      if (endDateObj < acquisitionDateObj && isEndDateValid === undefined) {
        setEndDateValidationError(
          'End date cannot be earlier than the Acquisition Date.'
        );
      } else {
        setEndDateValidationError('');
      }
    } else {
      setEndDateValidationError('');
    }
  }, [endDate]);

  const submitForm = (formValues) => {
    if (endDateValidationError === '') {
      onSave(formValues);
    }
  };

  return (
    <React.Fragment>
      <MotifModalHeader closeButtonProps={closeButtonProps}>
        <HeaderWrapper>
          <span>{initialValues.propertyName}</span>
        </HeaderWrapper>
      </MotifModalHeader>
      <MotifModalBody>
        <form onSubmit={handleSubmit(submitForm)} id="endPropertyForm">
          {errorMessage ? (
            formHelpers.showSubmissionError(
              errorMessage,
              handleDismissErrorMessage
            )
          ) : endDateValidationError ? (
            <MotifToast
              className="mt-2 mb-2"
              variant="error"
              onClose={handleDismissErrorMessage}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {endDateValidationError}
            </MotifToast>
          ) : null}

          <input name="id" type="hidden" label="id" />
          {initialValues.propertyTrackingID}
          <div className="row">
            <div className="field col-md-5">
              <br />
              <DateField
                name="endDate"
                label="End Date"
                placeholder="MM/DD/YYYY"
                validate={[formValidators.required, formValidators.isValidDate]}
              />
            </div>
          </div>
          <div>
            <p>
              Note: This property will be removed from all open periods with a
              start date after the date entered. For each impacted period, the
              property's trial balances will be deleted and test results will be
              recalculated.
            </p>
          </div>
        </form>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          variant="secondary"
          type="button"
          form="endPropertyForm"
          onClick={handleCloseEndForm}
        >
          Cancel
        </MotifButton>
        <MotifButton
          variant="primary"
          type="submit"
          form="endPropertyForm"
          disabled={submitting}
        >
          Save
        </MotifButton>
      </MotifModalFooter>
    </React.Fragment>
  );
};

EndPropertyForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  property: PropTypes.object,
  initialValues: PropTypes.object,
  errorMessage: PropTypes.string,
  submitting: PropTypes.bool,
  handleCloseEndForm: PropTypes.func.isRequired,
  handleDismissErrorMessage: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default reduxForm({ form: 'property' })(EndPropertyForm);
